import { useEffect, useLayoutEffect, useRef } from 'react';
import {
  BRAND_CACHE_KEY,
  type BrandInput,
  appliedBrand,
  appliedBrandTenant,
  applyBrand,
  clearBrand,
  clearBrandCache,
  deriveBrand,
  isBrandPrimary,
  readAppliedBrand,
  readBrandCache,
  registerBrandSyncer,
  releaseBrandSyncer,
  stashBrandHandover,
  takeBrandHandover,
  writeBrandCache,
} from './brand.js';
import { sessionDefault, setSessionDefaultTheme } from './theme.js';

/**
 * A branding read, in the shape `@tanstack/react-query` reports one.
 *
 * NOTE WHAT IS NOT IN IT: any tenant identity. The hook takes the acting tenant
 * as a separate argument and TRUSTS that this query is the branding read
 * belonging to that tenant. That trust is discharged by the caller, and in this
 * codebase it is a real guarantee rather than a hope: `@cusp/api-client` keys
 * every identity-scoped query `['identity', scope, …]`, so one institution's
 * cached branding is unreachable as another's, and `installIdentityCacheReset`
 * cancels the in-flight requests still carrying the old headers when the scope
 * changes. A caller that invents a key outside that convention breaks the
 * guarantee, and this hook cannot detect it — it would be handed one school's
 * colour alongside another school's id and paint it.
 */
export interface BrandQuery {
  status: 'pending' | 'success' | 'error';
  data: BrandInput | null | undefined;
}

/** Just enough of the acting identity: the id keys the cache, the slug names it. */
export interface BrandTenant {
  id: string;
  slug: string;
}

export interface BrandSyncOptions {
  /** Simulation may paint without replacing the real tenant’s first-paint cache. */
  persist?: boolean;
  /**
   * May the surface under this syncer wear the school's colour? Default true.
   *
   * THE PAINT AND THE TENANT ARE TWO QUESTIONS, and collapsing them is a bug
   * with a visible flash. `apps/web` serves the public front door and the
   * signed-in app from one bundle, and `/` and `/login` must stay Cusp gold
   * even for a signed-in reader. The obvious way to say that is to hand the
   * hook `tenant: null` on those paths — and it is wrong, because "no tenant"
   * also means "forget the school's default direction". A reader at a Canvas
   * school then loads `/` in Canvas (the pre-paint script and `initTheme()`
   * both read the cache) and watches it flip to Obsidian the moment React
   * commits: the front door is unbranded, which is right, and the wrong
   * colour scheme, which is not.
   *
   * So the caller passes the REAL tenant on every route and sets `paint` from
   * the path. With `paint: false` the document carries no `data-brand*` and no
   * `--brand-*`, whoever applied them — and the tenant's default direction and
   * its first-paint cache are maintained exactly as they are on a branded
   * surface. Flipping it back to true applies what is already loaded, in the
   * same commit; nothing waits for a refetch.
   */
  paint?: boolean;
}

/**
 * Keep the document's brand axis in step with the acting tenant.
 *
 * The hook lives here rather than in `brand.ts` for the same reason
 * `useTheme.ts` is not `theme.ts`: the store is a DOM store and has no React
 * in it, which is what lets the pre-paint script and the token test use it.
 *
 * It takes a query's `{ status, data }` and the acting tenant rather than
 * calling anything itself — `@cusp/theme` sits below `@cusp/api-client` in the
 * stack and must not learn how to make an HTTP request. `BrandInput` is
 * structural for the same reason (see `brand.ts`), and the identity the query
 * is partitioned by is the caller's to get right (see `BrandQuery` above).
 *
 * MOUNT ONE PER APP. The coordinator in `brand.ts` makes several instances
 * survivable, but it is a safety net, not the shape to build to: two live
 * syncers for two DIFFERENT tenants can only race, and the loser's colour stays
 * up until something re-renders. So `apps/web` mounts a single syncer in the
 * root route component and gates it on the router's location — the landing page
 * and `/login` are Cusp Media's own and must stay Cusp gold — with `paint`
 * rather than by withholding the tenant, for the reason `BrandSyncOptions`
 * gives. `apps/admin` is an authenticated bundle throughout, so one inside
 * `Frame` is already one per app, and it never passes the option at all.
 *
 * THE LIFECYCLE IS THE POINT, and each rule below is a specific way to show
 * the wrong school's colour:
 *
 *  · **a DIRECTION the acting tenant does not own is never left standing
 *    either.** The same rule as the paint below, run first because it applies
 *    on every surface: the front door suppresses colour, not the school's
 *    default direction, so `paint: false` is exactly where an orphaned
 *    direction would otherwise sit unchallenged for as long as the new
 *    tenant's read takes — which an error response makes forever. The store
 *    records the direction's owner (`SessionDefault` in `theme.ts` explains
 *    why), so this is a comparison too, and clearing re-applies the reader's
 *    own stored preference where they have one.
 *  · **a brand the acting tenant does not own is never left standing.** Checked
 *    first, on every run, in every query state — including pending, where the
 *    rule below would otherwise leave it. The document records its own owner
 *    (`AppliedBrand` in `brand.ts` explains why) so this is a comparison and
 *    not a guess, and it covers a workspace switch and a pre-paint that has
 *    been overtaken by the identity settling, with one line. Under
 *    `paint: false` the comparison is skipped and EVERY paint goes, ours
 *    included: the question there is not whose colour it is.
 *  · **pending leaves whatever is applied alone.** A reload paints the cached
 *    brand before React exists; treating the first `undefined` as "no brand"
 *    would clear it and flash Cusp gold on every single load of every branded
 *    school — the exact defect the cache exists to prevent. Safe only because
 *    of the ownership check above: what is left alone is provably ours. It
 *    leaves the school's default direction alone for the same reason: a
 *    pending read knows nothing the cache did not already say.
 *  · **error leaves it alone too.** A refetch that fails knows nothing new; the
 *    colour already on screen is still the best answer we have.
 *  · **success with no brand clears the cache too.** Not just the document:
 *    an entry left behind is one `initTheme()` away from resurrecting the
 *    school's default direction, and one reload away from the pre-paint script
 *    resurrecting its colours.
 *  · **the LAST syncer out clears.** The brand belongs to the authenticated
 *    shell; a shell that unmounts must not carry a school's colour onto the
 *    landing page. But a shell that unmounts while a sibling syncer is still
 *    mounted must not take the sibling's brand with it, which is what the
 *    registry in `brand.ts` decides. Under StrictMode the departure is a
 *    rehearsal rather than a real one, so the cleanup leaves a handover for the
 *    replayed setup to pick back up.
 *  · **a `storage` event re-applies**, so saving a new colour in the settings
 *    screen reaches the other tabs of the same origin — the same courtesy the
 *    theme store already extends to `cusp.theme`. Not under `paint: false`:
 *    another tab saving a colour is no reason to paint the front door.
 */
export function useBrandSync(
  query: BrandQuery,
  tenant: BrandTenant | null,
  options?: BrandSyncOptions,
): void {
  /**
   * The last committed inputs, for the registration effect below — which has no
   * dependency array on purpose (registering and releasing on every status
   * change would put the live count through zero on every render, and a count
   * that touches zero clears the document) and therefore cannot close over
   * them.
   */
  const latest = useRef<{ tenantId: string | null; settled: boolean; paint: boolean }>({
    tenantId: null,
    settled: false,
    paint: true,
  });

  const tenantId = tenant?.id ?? null;
  const slug = tenant?.slug ?? null;
  const paint = options?.paint ?? true;
  const persist = options?.persist ?? true;
  // Read through to primitives so an equal-but-new `data` object from the
  // query layer does not re-derive and re-write the cache on every render.
  const primary = query.status === 'success' ? (query.data?.primary ?? null) : null;
  const defaultTheme = query.status === 'success' ? (query.data?.defaultTheme ?? null) : null;
  const settled = query.status === 'success';

  // Layout, not passive: the clear on a workspace switch has to be on the
  // document before the browser paints the new workspace.
  useLayoutEffect(() => {
    // Committed before the registration effect below runs, on a first mount and
    // on a StrictMode replay alike: layout effects run in declaration order and
    // this one is declared first.
    latest.current = { tenantId, settled, paint };

    /**
     * THE DIRECTION'S OWNER, CHECKED BEFORE ANYTHING ELSE AND ON EVERY SURFACE.
     *
     * A school's default direction is as much the acting tenant's as its
     * colour is, and it outlives a workspace switch in exactly the same way —
     * with none of the paint's protection, because the paint branches below
     * are about the DOCUMENT and this is about the store. The front door is
     * where that bites: `paint: false` takes every colour down and returns, so
     * a reader who switches from a Canvas school to an Obsidian one while the
     * new school's read is pending (or erroring) keeps reading in the old
     * school's Canvas indefinitely.
     *
     * So it is one comparison, run for every `paint` and every query state,
     * against the owner the store recorded. Clearing does not mean Obsidian:
     * `setSessionDefaultTheme(null)` re-applies `storedTheme()`, so a reader
     * with a preference of their own keeps it and everyone else falls back to
     * ours. An unowned record (`tenantId: null`) never matches a real tenant,
     * which is the safe way for a direction of unknown provenance to end.
     */
    const direction = sessionDefault();
    if (direction !== null && direction.tenantId !== tenantId) setSessionDefaultTheme(null);

    if (!paint) {
      /**
       * A SURFACE NO SCHOOL MAY PAINT, and only the paint is suppressed.
       *
       * Every trace goes, whoever left it — a pre-paint script that ran on a
       * branded path before a client-side navigation landed here, this hook's
       * own work from the route before, a sibling's. Ownership is not the
       * question on the front door; there is no owner who would be allowed to
       * paint it.
       *
       * `clearBrand()` and not `forget()`: the school's default DIRECTION is
       * not a brand and is not suppressed here. It is what the pre-paint script
       * and `initTheme()` already applied from the cache on this very path, and
       * taking it down would turn an unbranded front door into a front door
       * that visibly changes colour scheme a beat after it loads.
       */
      clearBrand();
    } else {
      /**
       * OWNERSHIP FIRST, BEFORE ANY QUERY STATE GETS A SAY.
       *
       * Whatever is painted either belongs to the tenant we are acting in or it
       * does not, and that question is answerable without knowing who painted
       * it — the pre-paint script, an earlier run of this hook, and a sibling
       * syncer all write the owner down. A mismatch is torn down here and now;
       * a match is left completely alone, which is what keeps a correct
       * pre-paint from being flashed away on the very first commit.
       *
       * Either half of the paint missing counts as unowned, so a document
       * written by a build that predates `data-brand-tenant` is cleared rather
       * than trusted.
       */
      const owner = appliedBrandTenant();
      const branded = owner !== null || appliedBrand() !== null;
      if (branded && owner !== tenantId) forget();
    }

    if (tenantId === null || slug === null) {
      // No identity: there is nothing a brand could belong to. `forget()` again
      // rather than as an else-branch, because "unowned" above is about the
      // document and this is about the caller — a document that was already
      // clean still has to leave the session default cleared.
      forget();
      return;
    }
    if (!settled) return;

    if (!isBrandPrimary(primary)) {
      // The tenant has no brand — or has one this build cannot read, which is
      // the same thing to a renderer. Cusp gold, and no cache to go stale.
      clearBrand();
      setSessionDefaultTheme(null);
      // Only OUR entry. The cache holds one tenant's brand and another tab of
      // this origin can be acting elsewhere; removing an entry that is not
      // ours would take a school's first paint away from a tab that is
      // entitled to it. `readBrandCache` already answers null for anyone
      // else's, which makes this both the test and the ownership check.
      if (persist && readBrandCache(tenantId) !== null) clearBrandCache();
      return;
    }

    const tokens = deriveBrand(primary);
    // The direction and the cache are the tenant's, not the surface's, so they
    // are maintained on the front door exactly as they are anywhere else — the
    // reader who navigates on to `/courses` finds the brand already cached and
    // this effect re-runs with `paint` true and applies it in the same commit.
    if (paint) applyBrand({ tenantId, slug, tokens });
    setSessionDefaultTheme(defaultTheme, tenantId);
    if (persist) writeBrandCache({ tenantId, slug, tokens, defaultTheme });
  }, [tenantId, slug, settled, primary, defaultTheme, paint, persist]);

  /**
   * Registration, and the handover that makes a departure recoverable.
   *
   * A ROUTE SWAP REALLY DOES GO THROUGH ZERO, and the handover is what carries
   * the brand across it. React commits a swap as the departing tree's layout
   * cleanups FIRST, then the arriving tree's layout setups, so when the leaving
   * syncer was the only one its release empties the set before the arrival has
   * registered: "last one out" fires and the document is cleared. What puts the
   * school's colour back is the snapshot this cleanup stashes and the arriving
   * setup takes — both inside the same commit, so the clear is never a frame
   * anyone sees. The count is no help here and is not meant to be.
   *
   * WHAT THE COUNT IS FOR is concurrent instances: two syncers genuinely live
   * at once and one of them unmounts. The survivor's dependencies have not
   * changed, so nothing would ever re-apply what the departure took down —
   * which is why only the release that empties the set clears at all
   * (`brand.ts` explains the ownership rule).
   *
   * IT IS STILL A LAYOUT EFFECT, because the register/release pair has to sit
   * in the same phase as the paint/clear it guards. The sync effect above is
   * layout (a workspace switch has to be off the document before the browser
   * paints), so registering passively would move the departing release to
   * AFTER the arriving syncer had already painted: the count would hit zero
   * there, and "last one out" would clear a brand that had just been applied
   * correctly, by a component that no longer exists. The handover could not
   * undo that, because a settled arrival deliberately declines one.
   *
   * StrictMode's replay (cleanup → setup on the same instance) is the same
   * mechanism with one instance on both sides: the cleanup empties the set,
   * clears, and leaves the handover; the setup picks it back up. React 19 runs
   * that replay as layout cleanup → passive cleanup → layout setup → passive
   * setup, so the sync effect above has already re-run and set `latest` by the
   * time this reads it.
   */
  useLayoutEffect(() => {
    const token = registerBrandSyncer();

    // Consumed unconditionally so it can never be applied twice, and applied
    // only when it is this tenant's AND the read has not answered — a settled
    // read is better information than a snapshot of a guess, and the effect
    // above has already put it on the document.
    const handover = takeBrandHandover(latest.current.tenantId);
    if (handover !== null && !latest.current.settled) {
      // An unpainted surface takes back the DIRECTION and nothing else. The
      // brand half of the snapshot is either absent (nothing was painted) or
      // no longer wanted here (`paint` went false in the same commit that tore
      // it down), and re-applying it would undo the suppression.
      if (handover.brand !== null && latest.current.paint) applyBrand(handover.brand);
      setSessionDefaultTheme(handover.defaultTheme, handover.tenantId);
    }

    return () => {
      // A sibling is still mounted and the brand is its responsibility too.
      if (!releaseBrandSyncer(token)) return;
      // Read the document BEFORE clearing it — this is the only record of what
      // was there, and on a reload it is the pre-paint script's work, which
      // this hook never saw applied. It carries its own owner, so the next
      // syncer can check the handover against the tenant IT is mounted in
      // rather than against the tenant this one happened to be holding.
      const painted = readAppliedBrand();
      /**
       * THE DIRECTION IS KEYED BY ITS OWN OWNER, NOT BY WHO IS MOUNTED.
       *
       * A handover is restored to the tenant it names, so naming the mounted
       * tenant for a direction that belongs to somebody else launders one
       * school's Canvas into another school's mount and defeats the check
       * above — which is a real sequence and not a hypothetical: a `paint:
       * false` surface holds a direction and no colour, so the paint's owner
       * cannot stand in for it.
       *
       * The two owners agree wherever one syncer is mounted, because the
       * effect above has already cleared whichever of them did not match. Two
       * syncers for different tenants (a misconfiguration this hook asks
       * callers to avoid) can still leave them disagreeing, and there is no
       * single tenant that snapshot belongs to — so nothing is handed over at
       * all. Losing a direction across a remount costs one corrected frame;
       * restoring the wrong school's costs the frames until something else
       * re-renders.
       */
      const direction = sessionDefault();
      const owner = painted?.tenantId ?? direction?.tenantId ?? latest.current.tenantId;
      const split =
        painted !== null && direction !== null && painted.tenantId !== direction.tenantId;
      stashBrandHandover(
        owner === null || split
          ? null
          : { tenantId: owner, brand: painted, defaultTheme: direction?.theme ?? null },
      );
      forget();
    };
  }, []);

  useEffect(() => {
    const onStorage = (event: StorageEvent): void => {
      if (event.key !== BRAND_CACHE_KEY) return;
      // Another tab saving a colour is not a reason to paint a surface that is
      // not allowed one.
      if (!paint) return;
      // Only ever a RE-APPLY. Tabs of the same origin can be acting in
      // different tenants, so an entry written for someone else's tenant says
      // nothing about ours — and reading it as "no brand" would let one tab
      // strip another tab's colour.
      const cached = persist ? readBrandCache(tenantId) : null;
      if (cached === null) return;
      applyBrand({ tenantId: cached.tenantId, slug: cached.slug, tokens: cached.tokens });
      setSessionDefaultTheme(cached.defaultTheme, cached.tenantId);
    };
    window.addEventListener('storage', onStorage);
    return () => {
      window.removeEventListener('storage', onStorage);
    };
  }, [tenantId, paint, persist]);
}

/** Back to Cusp gold, and back to no school default direction. */
function forget(): void {
  clearBrand();
  setSessionDefaultTheme(null);
}
